import { useCallback, useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { SetRequired } from 'type-fest';

import {
  CardFieldItem,
  CardFieldItemProps,
} from '@/components/form/formAwareInputs/FormAwareCardRepeater/CardFieldItem';
import { DuplicatedFieldGroup } from '@/components/form/formAwareInputs/formAwareRepeater/FormAwareRepeater.types';
import { Card } from '@/components/layout/Card/Card';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useAiSuggestionsMatcherContext } from '@/modules/aiSuggestions/AISuggestionsMatcher/context/aiSuggestionsMatcher.context';

export interface DuplicatedCardFieldGroupProps<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
> extends Omit<
    CardFieldItemProps<FormShape, FieldArrayKeyName>,
    'i' | 'value' | 'onDeleteItemClick'
  > {
  group: SetRequired<
    DuplicatedFieldGroup<FormShape, FieldArrayKeyName>,
    'existingFieldWithIndex' | 'suggestedFieldWithIndex'
  >;
  fieldArrayKeyName: FieldArrayKeyName;
}

export function DuplicatedCardFieldGroup<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
>({
  group,
  fieldArrayKeyName,
  ...rest
}: DuplicatedCardFieldGroupProps<FormShape, FieldArrayKeyName>) {
  const trackUserEvent = useTrackUserEvent();
  const { resolveDuplicate, setDuplicateSelections } =
    useAiSuggestionsMatcherContext();

  const {
    fieldArrayProps: { remove },
  } = rest;

  const [selectedIndex, setSelectedIndex] = useState<number>(
    group.suggestedFieldWithIndex.index
  );

  useEffect(() => {
    setSelectedIndex(group.suggestedFieldWithIndex.index);
  }, [group.suggestedFieldWithIndex.index]);

  // Create onConfirm callbacks for each item
  const onConfirmSuggested = useCallback(() => {
    // Confirming the suggested field removes the existing field from the form array.
    remove(group.existingFieldWithIndex.index);
    resolveDuplicate(group.arrayFieldFullPath);
    trackUserEvent('ai_suggestion resolve duplicates', {
      removedVariant: 'existing',
    });
  }, [
    remove,
    group.existingFieldWithIndex.index,
    resolveDuplicate,
    group.arrayFieldFullPath,
    trackUserEvent,
  ]);

  const onConfirmExisting = useCallback(() => {
    // Confirming the existing field removes the suggested field from the form array.
    remove(group.suggestedFieldWithIndex.index);
    resolveDuplicate(group.arrayFieldFullPath);
    trackUserEvent('ai_suggestion resolve duplicates', {
      removedVariant: 'suggested',
    });
  }, [
    remove,
    group.suggestedFieldWithIndex.index,
    resolveDuplicate,
    group.arrayFieldFullPath,
    trackUserEvent,
  ]);

  // Update the context whenever selection changes
  useEffect(() => {
    const onConfirm =
      selectedIndex === group.suggestedFieldWithIndex.index
        ? onConfirmSuggested
        : onConfirmExisting;

    setDuplicateSelections((prev) => ({
      ...prev,
      [group.arrayFieldFullPath]: {
        selectedIndex,
        onConfirm,
      },
    }));
  }, [
    selectedIndex,
    group.arrayFieldFullPath,
    group.suggestedFieldWithIndex.index,
    setDuplicateSelections,
    onConfirmSuggested,
    onConfirmExisting,
  ]);

  return (
    <Card
      variant="filled-teal"
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        gap: 1,
      }}
      data-testid="duplicated-repeater-input-group"
    >
      <CardFieldItem<FormShape, FieldArrayKeyName>
        {...rest}
        key={group.suggestedFieldWithIndex.field[fieldArrayKeyName]}
        i={group.suggestedFieldWithIndex.index}
        value={group.suggestedFieldWithIndex.field}
        fieldArrayKeyName={fieldArrayKeyName}
        additionalItemRendererProps={{
          showAISparkleIcon: true,
          hideDeleteButton: true,
          duplicateResolutionConfigs: {
            isSelected: selectedIndex === group.suggestedFieldWithIndex.index,
            onSelect: () =>
              setSelectedIndex(group.suggestedFieldWithIndex.index),
            onConfirm: onConfirmSuggested,
          },
        }}
        // "spacing" is actually used as the bottom margin. We don't need bottom
        // margin here, since the Group Card already has bottom padding.
        spacing={0}
      />
      <CardFieldItem<FormShape, FieldArrayKeyName>
        {...rest}
        key={group.existingFieldWithIndex.field[fieldArrayKeyName]}
        i={group.existingFieldWithIndex.index}
        value={group.existingFieldWithIndex.field}
        fieldArrayKeyName={fieldArrayKeyName}
        additionalItemRendererProps={{
          hideLocateSuggestionIconOverride: true,
          duplicateResolutionConfigs: {
            isSelected: selectedIndex === group.existingFieldWithIndex.index,
            onSelect: () =>
              setSelectedIndex(group.existingFieldWithIndex.index),
            onConfirm: onConfirmExisting,
          },
        }}
        // "spacing" is actually used as the bottom margin. We don't need bottom
        // margin here, since the Group Card already has bottom padding.
        spacing={0}
      />
    </Card>
  );
}
